export const HALO_PAD = 48
export const ICON_HEIGHT = 32 / 1.5
export const MIX_TAU = 0.2
export const HALO_SPACING = 3
const HALO_LOOP_POINTS = 64

export const EDGE_COLORS = [
  [0.896269, 0.027321, 0.051269],
  [0, 0.40724, 0.048172],
  [0, 0.278894, 1],
]

const DX = [1, 1, 0, -1, -1, -1, 0, 1]
const DY = [0, 1, 1, 1, 0, -1, -1, -1]
const LUM_R = 0.2126
const LUM_G = 0.7152
const LUM_B = 0.0722

export function iconRadius(iconWidth) {
  return iconWidth * 0.5
}

export function iconAnchors(originX, originY, iconWidth, iconHeight) {
  return [
    { x: originX, y: originY + iconHeight / 2 },
    { x: originX + iconWidth / 2, y: originY + iconHeight },
    { x: originX + iconWidth, y: originY + iconHeight / 2 },
  ]
}

export function anchorWeight(distance, radius) {
  const span = Math.max(radius, 1)
  return (1 / (1 + distance / span)) ** 3
}

export function luminance(r, g, b) {
  return LUM_R * r + LUM_G * g + LUM_B * b
}

export function lightColor(point, anchors, radius, mixAmount) {
  const weights = anchors.map((anchor) =>
    anchorWeight(Math.hypot(point.x - anchor.x, point.y - anchor.y), radius)
  )
  const sum = weights.reduce((total, weight) => total + weight, 0) || 1
  let r = 0
  let g = 0
  let b = 0
  for (let index = 0; index < 3; index += 1) {
    const share = weights[index] / sum
    r += EDGE_COLORS[index][0] * share
    g += EDGE_COLORS[index][1] * share
    b += EDGE_COLORS[index][2] * share
  }
  const lum = luminance(r, g, b)
  if (lum > 1e-4) {
    const scale = 1 / lum
    r *= scale
    g *= scale
    b *= scale
  }
  const mix = Math.min(1, Math.max(0, mixAmount))
  return [1 + (r - 1) * mix, 1 + (g - 1) * mix, 1 + (b - 1) * mix]
}

export function stepMix(mix, target, dt, tau = MIX_TAU) {
  const elapsed = Math.max(0, dt)
  if (tau <= 0) return target
  const next = mix + (target - mix) * (1 - Math.exp(-elapsed / tau))
  if (Math.abs(target - next) < 1e-4) return target
  return next
}

function isOpaque(pixels, width, height, x, y) {
  if (x < 0 || y < 0 || x >= width || y >= height) return false
  return pixels[(y * width + x) * 4 + 3] > 16
}

function isBoundary(pixels, width, height, x, y) {
  if (!isOpaque(pixels, width, height, x, y)) return false
  return (
    !isOpaque(pixels, width, height, x - 1, y) ||
    !isOpaque(pixels, width, height, x + 1, y) ||
    !isOpaque(pixels, width, height, x, y - 1) ||
    !isOpaque(pixels, width, height, x, y + 1)
  )
}

function emptyDir(isOn, x, y) {
  for (let dir = 0; dir < 8; dir += 1) {
    if (!isOn(x + DX[dir], y + DY[dir])) return dir
  }
  return 0
}

function traceLoop(isOn, sx, sy) {
  const loop = []
  let x = sx
  let y = sy
  let dir = emptyDir(isOn, x, y)
  const limit = 20000
  for (let step = 0; step < limit; step += 1) {
    loop.push({ x, y })
    let found = false
    for (let turn = 1; turn <= 8; turn += 1) {
      const nextDir = (dir + turn) % 8
      const nx = x + DX[nextDir]
      const ny = y + DY[nextDir]
      if (isOn(nx, ny)) {
        x = nx
        y = ny
        dir = (nextDir + 4) % 8
        found = true
        break
      }
    }
    if (!found || (x === sx && y === sy)) break
  }
  return loop
}

function resample(loop, maxPoints) {
  if (loop.length <= maxPoints) return loop
  const out = []
  const step = loop.length / maxPoints
  for (let index = 0; index < maxPoints; index += 1) {
    out.push(loop[Math.floor(index * step)])
  }
  return out
}

export function traceContours(pixels, width, height) {
  const isOn = (x, y) => isOpaque(pixels, width, height, x, y)
  const visited = new Set()
  const loops = []
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const key = `${x},${y}`
      if (visited.has(key) || !isBoundary(pixels, width, height, x, y)) continue
      const loop = traceLoop(isOn, x, y)
      if (loop.length >= 8) loops.push(loop)
      for (const point of loop) visited.add(`${point.x},${point.y}`)
    }
  }
  return loops
}

export function buildHaloGeometry(pixels, width, height, pad) {
  return traceContours(pixels, width, height).map((loop) =>
    resample(loop, HALO_LOOP_POINTS).map((point) => ({
      x: point.x + pad + 0.5,
      y: point.y + pad + 0.5,
    }))
  )
}

function pointOnPill(distance, width, height, radius, straight, arc) {
  let rest = distance
  if (rest < straight) return { x: radius + rest, y: 0 }
  rest -= straight
  if (rest < arc) {
    const angle = -Math.PI / 2 + (rest / arc) * Math.PI
    return {
      x: width - radius + Math.cos(angle) * radius,
      y: radius + Math.sin(angle) * radius,
    }
  }
  rest -= arc
  if (rest < straight) return { x: width - radius - rest, y: height }
  rest -= straight
  const angle = Math.PI / 2 + (rest / Math.max(arc, 1e-6)) * Math.PI
  return {
    x: radius + Math.cos(angle) * radius,
    y: radius + Math.sin(angle) * radius,
  }
}

export function pillLoops(width, height, pad = 0, spacing = HALO_SPACING) {
  const boxWidth = Math.max(1, width)
  const boxHeight = Math.max(1, Math.min(height, boxWidth))
  const radius = boxHeight / 2
  const straight = boxWidth - boxHeight
  const arc = Math.PI * radius
  const perimeter = 2 * straight + 2 * arc
  const count = Math.max(HALO_LOOP_POINTS, Math.ceil(perimeter / Math.max(spacing, 0.5)))
  const loop = []
  for (let index = 0; index < count; index += 1) {
    const point = pointOnPill((index / count) * perimeter, boxWidth, boxHeight, radius, straight, arc)
    loop.push({ x: point.x + pad, y: point.y + pad })
  }
  return [loop]
}

export function haloTimeScale(pointCount) {
  return Math.max(1, pointCount) / HALO_LOOP_POINTS
}

function toSrgbChannel(channel) {
  const value = Math.min(1, Math.max(0, channel))
  if (value <= 0.0031308) return 12.92 * value
  return 1.055 * value ** (1 / 2.4) - 0.055
}

function cssColor(linear, alpha) {
  const r = Math.round(toSrgbChannel(linear[0]) * 255)
  const g = Math.round(toSrgbChannel(linear[1]) * 255)
  const b = Math.round(toSrgbChannel(linear[2]) * 255)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function bandBrightness(index, count, time) {
  let level = 0.35
  for (let band = 0; band < 3; band += 1) {
    const center = (time * (10 + band * 4) + (band * count) / 3) % count
    let delta = Math.abs(index - center)
    delta = Math.min(delta, count - delta)
    const width = Math.max(4, count * 0.16)
    const core = width * 0.4
    const falloff = delta <= core ? 1 : Math.max(0, 1 - (delta - core) / (width - core))
    level = Math.max(level, falloff)
  }
  return Math.min(1, level)
}

export function drawHaloFrame(ctx, frame) {
  const { cssWidth, cssHeight, dpr, loops, anchors, radius, mix, time } = frame
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.globalCompositeOperation = "source-over"
  ctx.clearRect(0, 0, cssWidth, cssHeight)
  if (mix < 0.004 || loops.length === 0) return

  ctx.save()
  for (const loop of loops) {
    const count = loop.length
    for (let index = 0; index < count; index += 1) {
      const point = loop[index]
      const brightness = bandBrightness(index, count, time)
      const color = lightColor(point, anchors, radius, 1)
      const alpha = Math.min(1, mix * (0.72 + 0.28 * brightness))
      const ink = cssColor(color, alpha)
      ctx.shadowBlur = 14
      ctx.shadowColor = ink
      ctx.fillStyle = ink
      ctx.beginPath()
      ctx.arc(point.x, point.y, 4.5, 0, Math.PI * 2)
      ctx.fill()
    }
  }
  ctx.restore()
}
